import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/types";
import { fetchBundle } from "@/api/bundles";
import {
  BundleDetail,
  BundleCourse,
  BundleEvent,
  BundleResource,
} from "@/api/bundles/types";

export interface BundleDetailState {
  bundle: BundleDetail | null;
  relatedContent: {
    courses: BundleCourse[];
    events: BundleEvent[];
    resources: BundleResource[];
  };
  loading: boolean;
  error: string | null;
}

const initialState: BundleDetailState = {
  bundle: null,
  relatedContent: {
    courses: [],
    events: [],
    resources: [],
  },
  loading: false,
  error: null,
};

const bundleDetailSlice = createSlice({
  name: "bundleDetail",
  initialState,
  reducers: {
    setBundle: (state, action: PayloadAction<BundleDetail | null>) => {
      state.bundle = action.payload;
    },
    setRelatedContent: (
      state,
      action: PayloadAction<{
        courses: BundleCourse[];
        events: BundleEvent[];
        resources: BundleResource[];
      }>,
    ) => {
      state.relatedContent = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    resetState: (state) => {
      Object.assign(state, initialState);
    },
  },
});

/**
 * Async thunk to fetch bundle details
 */
export const loadBundleDetail =
  (bundleId: string): AppThunk =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));

      const response = await fetchBundle(bundleId);

      dispatch(setBundle(response.bundle));
      dispatch(setRelatedContent(response.relatedContent));
    } catch (error) {
      console.error("Error fetching bundle details:", error);
      dispatch(
        setError(
          error instanceof Error
            ? error.message
            : "Failed to fetch bundle details",
        ),
      );
      dispatch(setBundle(null));
    } finally {
      dispatch(setLoading(false));
    }
  };

export const {
  setBundle,
  setRelatedContent,
  setLoading,
  setError,
  resetState,
} = bundleDetailSlice.actions;

export default bundleDetailSlice.reducer;
